import React, {useRef, useState, useEffect} from 'react';
import {Container, ContentLayout, SpaceBetween, Spinner, Box, ButtonDropdown, Button, Header} from '@amzn/awsui-components-react';
import {Notification} from '../navigation/page-layout';
import {ServiceResource, useAuth} from '../hooks/use-auth';
import BoscoExplainerTable from './bosco-explainer/bosco-explainer-table';
import BoscoExplainerLineChart from './bosco-explainer/bosco-explainer-line-chart';
import BoscoExplainerFilters from './bosco-explainer/bosco-explainer-filters';
import {useGetForecastIdsMetadata} from '../hooks/use-forecast-store-api';
import {translateErrorToReactNode} from '../common';
import {useGetExplainabilityAgg, useGetExplainabilityMetadata} from '../hooks/use-analytics-api';
import {ForecastIdMetaData} from '@amzn/f3-excelsior-forecast-store-lambda/clients/f3excelsiorforecaststorelambda';
import PortalLoader from '../common/PortalLoader';
import {
    ExplainabilityFilterValuesList,
    NodeDetails,
} from '@amzn/f3-excelsior-analytics-api/clients/analyticsapiforexcelsioranalytics';
import _ from 'lodash';

const MODEL_NAME = 'bosco';

const DAYS_TO_INSPECT_FORECAST_METADATA = 28; // 4 weeks
const DAYS_TO_INSPECT_FORECAST = 28; // 4 weeks

const MODEL_COMPONENT_COLUMNS = [
    'shap_global_level_mean',
    'shap_global_seasonal_mean',
    'shap_global_error_mean',
    'shap_series_level_mean',
    'shap_series_seasonal_mean',
    'shap_holiday_mean',
    'shap_series_error_mean_with_residual',
];

const FORECAST_COLUMNS = ['raw_forecast', 'cd_mean', 'final_forecast'];

const POST_PROCESS_COLUMNS = [
    'zip_mapping_contribution',
    'demand_curve_contribution',
    'store_hour_normalization_contribution',
    'inference_publishing_contribution',
];

const FINAL_TABLE_COLUMNS = [
    'cd_mean',
    'shap_global_level_mean',
    'shap_global_seasonal_mean',
    'shap_global_error_mean',
    'shap_series_level_mean',
    'shap_series_seasonal_mean',
    'shap_series_error_mean_with_residual',
    'shap_holiday_mean',
    'raw_forecast',
    'post_process_contributions',
    'final_forecast',
];

const FRIENDLY_NAME_MAPPING = {
    shap_global_level_mean: 'Global Trend',
    shap_global_seasonal_mean: 'Global Day-of-Week',
    shap_global_error_mean: 'Global Noise',
    shap_series_level_mean: 'Store Specific Trend',
    shap_series_seasonal_mean: 'Store Specific Day-of-Week',
    shap_holiday_mean: 'Store Specific Holiday Impact and Annual Seasonality',
    shap_series_error_mean_with_residual: 'Store Specific Noise',
    zip_mapping_contribution: 'Zip Mapping',
    demand_curve_contribution: 'Demand Curve Application',
    store_hour_normalization_contribution: 'Store Hour Normalization',
    inference_publishing_contribution: 'Inference Publishing',
    raw_forecast: 'Model Forecast',
    cd_mean: 'Training CD Average',
    final_forecast: 'Unconstrained Baseline Forecast',
    post_process_contributions: 'Post-Process Contributions',
};

type SelectedFilter = {
    nodes: NodeDetails;
    granularity: any;
    balanceDateStart: string;
    balanceDateEnd: string;
};

type FilterValues = {
    nodes: NodeDetails;
    granularities: any;
    balanceDateEarliest: string;
    balanceDateLatest: string;
};

type Records = {
    [key: string]: string | number;
};

export function BoscoExplainer(props: {pushNotification: (notification: Notification) => void}) {
    const auth = useAuth();
    const initialized = useRef<boolean>(false);
    const isLoadingFilters = useRef<boolean>(true);
    const isLoadingData = useRef<boolean>(true);
    const isLoadingForecasts = useRef<boolean>(true);
    const isLoadingDataError = useRef<boolean>(false);

    const [forecastMetadata, setForecastMetadata] = useState<ForecastIdMetaData[]>([]);
    const [data, setData] = useState<Records[]>([]);
    const [explainabilityMetadata, setExplainabilityMetadata] = useState<ExplainabilityFilterValuesList>([]);
    const [filterValues, setFilterValues] = useState<FilterValues>({
        nodes: [],
        granularities: {},
        balanceDateEarliest: '',
        balanceDateLatest: '',
    });
    const [selectedFilter, setSelectedFilter] = useState<SelectedFilter>({
        nodes: filterValues.nodes,
        granularity: {},
        balanceDateStart: '',
        balanceDateEnd: '',
    });
    const [selectedForecast, setSelectedForecast] = useState<ForecastIdMetaData>();

    const tenant = {
        business: auth.authInformation!.current!.businessId,
        country: auth.authInformation!.current!.country,
        flow: auth.authInformation!.current!.flow,
    };

    function createErrorListener<T>(header: string) {
        return (e: any) => {
            props.pushNotification({
                type: 'error',
                content: translateErrorToReactNode(e),
                header,
            });
        };
    }

    const clientConfiguration = auth.authInformation!.getCurrentServiceEndpoint(ServiceResource.ExplainabilityView);

    const currentTime = new Date();
    const startTime = new Date(new Date(currentTime).setDate(currentTime.getDate() - DAYS_TO_INSPECT_FORECAST_METADATA));

    const forecastStoreViewClientConfiguration = auth.authInformation!.getCurrentServiceEndpoint(ServiceResource.ForecastStoreView);

    const {
        status: getForecastIdsMetadataStatus,
        value: forecastIdsMetadata,
        execute: getForecastIdsMetadata,
    } = useGetForecastIdsMetadata(
        forecastStoreViewClientConfiguration,
        {
            businessId: auth.authInformation!.current!.businessId,
            country: auth.authInformation!.current!.country,
            flow: auth.authInformation!.current!.flow,
            startTime: startTime.valueOf().toString(),
            endTime: currentTime.valueOf().toString(),
        },
        false,
        createErrorListener('GetForecastIdsMetadata failed'),
        [auth]
    );

    const {
        execute: executeGetExplainabilityAgg,
        status: getExplainabilityAggStatus,
        value: retrieveGetExplainabilityAggResponse,
    } = useGetExplainabilityAgg(clientConfiguration, createErrorListener('GetExplainabilityAgg failed'), [auth]);

    const {
        execute: executeGetExplainabilityMetadata,
        status: getExplainabilityMetadataStatus,
        value: retrieveGetExplainabilityMetadataOutput,
    } = useGetExplainabilityMetadata(clientConfiguration, createErrorListener('GetExplainabilityMetadata failed'), [auth]);

    isLoadingForecasts.current = ['idle', 'pending'].includes(getForecastIdsMetadataStatus);
    isLoadingFilters.current = ['idle', 'pending'].includes(getExplainabilityMetadataStatus);
    isLoadingData.current = ['idle', 'pending'].includes(getExplainabilityAggStatus);
    isLoadingDataError.current = getExplainabilityAggStatus === 'error';

    if (!initialized.current) {
        getForecastIdsMetadata();
        initialized.current = true;
    }

    useEffect(() => {
        if (!isLoadingForecasts.current && forecastIdsMetadata && forecastIdsMetadata.length > 0) {
            setForecastMetadata(forecastIdsMetadata);
            setSelectedForecast(forecastIdsMetadata[0]);
        }
    }, [isLoadingForecasts.current]);

    useEffect(() => {
        if (selectedForecast)
            executeGetExplainabilityMetadata({
                ...tenant,
                filter: JSON.stringify({
                    modelRunId: selectedForecast.modelRunId,
                    modelName: MODEL_NAME,
                }),
            });
    }, [selectedForecast]);

    useEffect(() => {
        if (!isLoadingFilters.current && retrieveGetExplainabilityMetadataOutput?.result) {
            const explainabilityFilterValues = retrieveGetExplainabilityMetadataOutput.result;
            setExplainabilityMetadata(explainabilityFilterValues);
            const granularitiesList = explainabilityFilterValues.map((value) => value.granularity);
            const granularitiesAgg = granularitiesList.reduce((acc, granularity) => {
                for (const key in granularity) {
                    if (!acc[key]) {
                        acc[key] = new Set(); // Initialize the array for the key if it doesn't exist
                    }
                    acc[key].add(granularity[key]); // Add the value to the corresponding array
                }
                return acc;
            }, {} as Record<string, Set<string>>);

            const setGranularity = Object.fromEntries(
                Object.entries(granularitiesAgg || {}).map(([key, set]) => [key, [...set][0]])
            );

            const explainabilityFilterValue = explainabilityFilterValues.find((value) =>
                _.isEqual(value.granularity, setGranularity)
            );

            setFilterValues({
                nodes: explainabilityFilterValue?.nodes || [],
                granularities: granularitiesAgg || {},
                balanceDateEarliest: explainabilityFilterValue?.balanceDateEarliest || '',
                balanceDateLatest: explainabilityFilterValue?.balanceDateLatest || '',
            });

            const selectedNodes = explainabilityFilterValue?.nodes || [];

            const balanceDateStart = explainabilityFilterValue?.balanceDateEarliest;
            let balanceDateEnd = '';
            if (balanceDateStart) {
                const balanceDateStartAsDate = new Date(balanceDateStart);
                balanceDateStartAsDate.setDate(balanceDateStartAsDate.getDate() + DAYS_TO_INSPECT_FORECAST); //4 weeks
                balanceDateEnd = new Date(balanceDateStartAsDate).toISOString().substring(0, 10);
            }

            setSelectedFilter({
                nodes: selectedNodes,
                granularity: setGranularity,
                balanceDateStart: balanceDateStart || '',
                balanceDateEnd: balanceDateEnd,
            });

            executeGetExplainabilityAgg({
                ...tenant,
                filter: JSON.stringify({
                    modelRunId: selectedForecast?.modelRunId,
                    modelName: MODEL_NAME,
                    granularity: setGranularity,
                    balanceDateStart: balanceDateStart,
                    balanceDateEnd: balanceDateEnd,
                }),
            });
        }
    }, [isLoadingFilters.current]);

    useEffect(() => {
        const explainabilityFilterValue = explainabilityMetadata.find((value) =>
            _.isEqual(value.granularity, selectedFilter.granularity)
        );

        setFilterValues((prev) => ({
            ...prev,
            nodes: explainabilityFilterValue?.nodes || [],
            balanceDateEarliest: explainabilityFilterValue?.balanceDateEarliest || '',
            balanceDateLatest: explainabilityFilterValue?.balanceDateLatest || '',
        }));

        const selectedNodes = explainabilityFilterValue?.nodes || [];

        const balanceDateStart = explainabilityFilterValue?.balanceDateEarliest;
        let balanceDateEnd = '';
        if (balanceDateStart) {
            const balanceDateStartAsDate = new Date(balanceDateStart);
            balanceDateStartAsDate.setDate(balanceDateStartAsDate.getDate() + DAYS_TO_INSPECT_FORECAST); //4 weeks
            balanceDateEnd = new Date(balanceDateStartAsDate).toISOString().substring(0, 10);
        }

        setSelectedFilter((prev) => ({
            ...prev,
            nodes: selectedNodes,
            balanceDateStart: balanceDateStart || '',
            balanceDateEnd: balanceDateEnd,
        }));
    }, [explainabilityMetadata, selectedFilter.granularity]);

    useEffect(() => {
        if (!isLoadingData.current && retrieveGetExplainabilityAggResponse?.records) {
            const records = retrieveGetExplainabilityAggResponse.records;
            const data = records.map((record) => {
                const {balanceDate, insights} = record;
                return {
                    ...insights,
                    balanceDate: new Date(balanceDate).toISOString().substring(0, 10),
                };
            });
            setData(data);
        }
    }, [isLoadingData.current]);

    useEffect(() => {
        if (isLoadingDataError.current) {
            setData([]);
        }
    }, [isLoadingDataError.current]);

    if (isLoadingForecasts.current && isLoadingFilters.current) {
        return <PortalLoader />;
    }

    function renderVisualizations() {
        if (isLoadingData.current) {
            return (
                <Container data-testid="data-loader">
                    <Box textAlign="center" color="inherit">
                        <Spinner size="large" />
                    </Box>
                </Container>
            );
        } else {
            return (
                <SpaceBetween direction="vertical" size="xxl">
                    <BoscoExplainerLineChart
                        data={data}
                        forecastColumns={FORECAST_COLUMNS}
                        contributionColumns={[...MODEL_COMPONENT_COLUMNS, ...POST_PROCESS_COLUMNS]}
                        friendlyNameMapping={FRIENDLY_NAME_MAPPING}
                    />
                    <BoscoExplainerTable
                        data={data}
                        finalColumns={FINAL_TABLE_COLUMNS}
                        postProcessColumns={POST_PROCESS_COLUMNS}
                        modelComponentColumns={MODEL_COMPONENT_COLUMNS}
                        friendlyNameMapping={FRIENDLY_NAME_MAPPING}
                    />
                </SpaceBetween>
            );
        }
    }

    return (
        <ContentLayout defaultPadding>
            <SpaceBetween direction="vertical" size="xxl">
                <div>
                    <SpaceBetween direction="horizontal" size="xxl">
                        <ButtonDropdown
                            data-testid="select-forecast"
                            items={forecastMetadata.map((forecast) => ({
                                text: new Date(forecast.createdTime).toLocaleString(),
                                id: forecast.forecastId,
                            }))}
                            onItemClick={async (item) => {
                                const foundIndex = forecastMetadata.findIndex((forecast) => forecast.forecastId === item.detail.id);
                                if (foundIndex !== -1) {
                                    setSelectedForecast(forecastMetadata[foundIndex]);
                                }

                                isLoadingFilters.current = true;
                            }}
                            loading={isLoadingFilters.current}
                            disabled={isLoadingFilters.current}
                        >
                            Select Forecast
                        </ButtonDropdown>
                        <Button
                            data-testid="refresh"
                            iconName="refresh"
                            variant="normal"
                            onClick={async () => {
                                await getForecastIdsMetadata();
                            }}
                        />
                    </SpaceBetween>
                </div>
                <Header
                    description={`Forecast Start Date: ${selectedForecast && selectedForecast.forecastStartDate.substring(0, 10)}`}
                >
                    Forecast Generated on{' '}
                    {selectedForecast &&
                        new Date(selectedForecast.createdTime).toLocaleString('en-us', {
                            weekday: 'short',
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            timeZoneName: 'short',
                        })}
                </Header>
                <BoscoExplainerFilters
                    filterValues={filterValues}
                    selectedFilter={selectedFilter}
                    setSelectedFilter={setSelectedFilter}
                    isLoadingData={isLoadingData.current}
                    isLoadingFilterData={isLoadingFilters.current}
                    onApply={() => {
                        isLoadingData.current = true;
                        executeGetExplainabilityAgg({
                            ...tenant,
                            filter: JSON.stringify({
                                modelRunId: selectedForecast?.modelRunId,
                                modelName: MODEL_NAME,
                                granularity: selectedFilter.granularity,
                                nodes: selectedFilter.nodes.map((node: any) => node.nodeId).join('|'),
                                balanceDateStart: selectedFilter.balanceDateStart,
                                balanceDateEnd: selectedFilter.balanceDateEnd,
                            }),
                        });
                    }}
                />
                {renderVisualizations()}
            </SpaceBetween>
        </ContentLayout>
    );
}
